import Flex from "@/components/Flex/Flex";
import styles from "./DimensionsAndWeightRow.module.css";
import { ReactNode, useEffect, useState } from "react";
import { FbaBox } from "@/contexts/ShipmentContext";
import IconInfo from "@/components/icons/IconInfo";
import Checkbox from "@/components/forms/Checkbox/Checkbox";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import InputNumber from "@/components/forms/InputNumber/InputNumber";
import { DimensionsAndWeight, getDefaultDimensionsAndWeight } from "../utils";
import RadioInput from "@/components/forms/RadioInput";
import { useForm } from "@/components/Form";

export function equals(a: DimensionsAndWeight, b: DimensionsAndWeight) {
  return a.length === b.length 
    && a.width === b.width
    && a.height === b.height
    && a.weight === b.weight;
}

export function boxToDimensionsAndWeight(box: FbaBox): DimensionsAndWeight {
  return { length: box.length, width: box.width, height: box.height, weight: box.weight };
}

export interface DimensionsAndWeightRowProps {
  children?: ReactNode;
  value?: DimensionsAndWeight;
  onChange?: (value: DimensionsAndWeight) => void;

  disabled?: boolean;

  showWeight2?: boolean;
  showLabels?: boolean;
  autofocus?: boolean;
  showMaxWeightAndDimensionsTooltip?: boolean;

  // very special case. US pallets must be 40in by 48in in the lateral dimensions,
  // but it's probably confusing to just hide the length and width dimensions, so
  // we selectively disable only the first two, and show a tooltip.
  disableLengthAndWidth?: boolean;

  maxWeight?: number;
  minWeight?: number;
  maxDimension?: number;
  palletStackable?: boolean;
  palletStackableOnChange?: (value: boolean) => void;
  oversizedControls?: boolean;
  oversizedTooltip?: boolean;

  setAsDefaultControls?: boolean;
  isDefault?: boolean;
  makeDefault?: () => void;
  tabIndexesStart?: number;
}

export default function DimensionsAndWeightRow({
  children,
  onChange,
  value: value1,
  disabled = false,
  showWeight2 = true,
  showLabels = true,
  autofocus = false,
  minWeight = 0,
  maxWeight = 50,
  maxDimension = 25,
  showMaxWeightAndDimensionsTooltip = false,
  disableLengthAndWidth = false,
  palletStackable,
  palletStackableOnChange,
  oversizedControls = true,
  oversizedTooltip = true,
  setAsDefaultControls = true,
  isDefault = false,
  tabIndexesStart = undefined,
  makeDefault,
}: DimensionsAndWeightRowProps) {
  const formValue = value => ({
    oversized: value.height > maxDimension || value.width > maxDimension || value.length > maxDimension || value.weight > maxWeight,
    length: disableLengthAndWidth ? 40 : value.length,
    width: disableLengthAndWidth ? 48 : value.width,
    height: value.height,
    weight: value.weight,

    // 1 & 2 instead of 'lbs' and 'oz' because longer-term we may support metric.
    weight1: Math.floor(value.weight),
    weight2: Math.round(((value.weight % 1) * 16)*10.0)/10.0,
  });

  const form = useForm(formValue(value1));

  useEffect(() => form.setValue(formValue(value1)), [value1]);

  useEffect(() => {
    if (!form.value.oversized) {
      form.setFieldValue("length", Math.min(form.value.length, maxDimension));
      form.setFieldValue("width", Math.min(form.value.width, maxDimension));
      form.setFieldValue("height", Math.min(form.value.height, maxDimension));
      form.setFieldValue("weight", Math.min(form.value.weight, maxWeight));
    }
  }, [form.value.oversized]);

  useEffect(() => {
    form.setFieldValue("weight", form.value.weight1 + form.value.weight2 / 16);
  }, [form.value.weight1, form.value.weight2]);

  useEffect(() => {
    let weight = form.value.weight;
    if (oversizedControls) {
      form.setFieldValue("oversized", form.value.height > maxDimension || form.value.width > maxDimension || form.value.length > maxDimension || form.value.weight > maxWeight);
    } else {
      weight = Math.min(form.value.weight, maxWeight);
      form.setFieldValue("weight", weight);
      form.setFieldValue("length", Math.min(form.value.length, maxDimension));
      form.setFieldValue("width", Math.min(form.value.width, maxDimension));
      form.setFieldValue("height", Math.min(form.value.height, maxDimension));
    }
    form.setFieldValue("weight1", Math.floor(weight));
    form.setFieldValue("weight2", Math.round(((weight % 1) * 16)*10.0)/10.0);

    onChange?.({
      length: form.value.length,
      width: form.value.width,
      height: form.value.height,
      weight: form.value.weight,
    });
  }, [form.value.height, form.value.width, form.value.length, form.value.weight]);

  return (
    <Flex direction="column" style={{flexGrow: "1"}}>
      <Flex style={{marginBottom: showLabels ? "0.5rem" : undefined}} gap={2}>
        <Flex direction="column" justify="start" style={{flexBasis: "40%"}}>
          {showLabels && (
            <Flex style={{ marginBottom: "6px" }}>
              {showMaxWeightAndDimensionsTooltip && (
                <Tooltip
                  title={disableLengthAndWidth
                    ? `Pallets in the US marketplace must be 40in by 48in length and width. Height must be between 20in and ${maxDimension}in`
                    : `Must be less than ${maxDimension}in in all dimensions unless your product is oversized (a single unit is taller than ${maxDimension})`
                  }
                >
                  <IconInfo style={{ marginRight: "4px" }} />
                </Tooltip>
              )}
              <p className="small-text">
                Dimensions
              </p>
            </Flex>
          )}
          <Flex align="center">
            <Tooltip title="Length" placement = "top" delayMs={100}>
              <InputNumber
                tabIndex={tabIndexesStart ? tabIndexesStart + 0 : undefined}
                className={styles.dimensionInput}
                centerText={false}
                disabled={disabled}
                isReadonly={disableLengthAndWidth}
                showControls={false}
                autoFocus={autofocus}
                suffix={<span className="weak">in</span>}
                min={0}
                max={oversizedControls ? 999 : maxDimension}
                value={form.value.length}
                onChange={value => form.setFieldValue("length", value)}
              />
            </Tooltip>
            <span style={{userSelect: "none"}} className="weak">&nbsp;&times;&nbsp;</span>
            <Tooltip title="Width" placement = "top" delayMs={100}>
              <InputNumber
                tabIndex={tabIndexesStart ? tabIndexesStart + 1 : undefined}
                className={styles.dimensionInput}
                centerText={false}
                disabled={disabled}
                isReadonly={disableLengthAndWidth}
                showControls={false}
                suffix={<span className="weak">in</span>}
                min={0}
                max={oversizedControls ? 999 : maxDimension}
                value={form.value.width}
                onChange={value => form.setFieldValue("width", value)}
              />
            </Tooltip>
            <span style={{userSelect: "none"}} className="weak">&nbsp;&times;&nbsp;</span>
            <Tooltip title="Height" placement = "top" delayMs={100}>
              <InputNumber
                tabIndex={tabIndexesStart ? tabIndexesStart + 2 : undefined}
                className={styles.dimensionInput}
                centerText={false}
                disabled={disabled}
                showControls={false}
                suffix={<span className="weak">in</span>}
                min={disableLengthAndWidth ? 20 : 0}
                max={oversizedControls ? 999 : maxDimension}
                value={form.value.height}
                onChange={value => form.setFieldValue("height", value)}
              />
            </Tooltip>
          </Flex>
        </Flex>
        {!showLabels && <span style={{width: "1px", alignSelf: "stretch", backgroundColor: "var(--static-divider-standard)"}}></span>}
        <Flex direction="column" justify="center" align="start" grow={3}>
          {showLabels && (
            <Flex style={{ marginBottom: "6px" }}>
              {showMaxWeightAndDimensionsTooltip && (
                <Tooltip title={disableLengthAndWidth ? `Pallets in the US marketplace must be less than 1,500lbs` : `Must be less than ${50}lbs unless the box is oversized`}>
                  <IconInfo style={{ marginRight: "4px" }} />
                </Tooltip>
              )}
              <p className="small-text">
                Weight
              </p>
            </Flex>
          )}
          <Flex align="center" gap={2}>
            <InputNumber
              tabIndex={tabIndexesStart ? tabIndexesStart + 3 : undefined}
              className={styles.weightInput}
              centerText={false}
              disabled={disabled}
              showControls={false}
              suffix={<span className="weak">lb</span>}
              min={minWeight}
              max={oversizedControls ? 9999 : maxWeight}
              value={form.value.weight1}
              onChange={value => form.setFieldValue("weight1", value)}
            />
            {showWeight2 && (
              <InputNumber
                tabIndex={tabIndexesStart ? tabIndexesStart + 4 : undefined}
                className={styles.weightInput}
                disabled={disabled}
                centerText={false}
                showControls={false}
                suffix={<span className="weak">oz</span>}
                min={0}
                max={16}
                value={form.value.weight2}
                onChange={value => form.setFieldValue("weight2", Math.round(value*10)/10)}
              />
            )}
            {(palletStackable !== undefined && !!palletStackableOnChange) && (
              <label>
                <Checkbox
                  disabled={disabled}
                  value={palletStackable}
                  onChange={value => palletStackableOnChange(value)}
                />
                Stackable
              </label>
            )}
            {oversizedControls && (
              <label>
                <Checkbox
                  disabled={disabled}
                  value={form.value.oversized}
                  onChange={ value => { if (!value) form.setFieldValue("oversized", value) }}
                />
                Oversized
              </label>
            )}
            {oversizedTooltip && (
            <a href="https://sellercentral.amazon.com/help/hub/reference/G63ZD2BUDXF28WEC" target="_blank" style={{display: "flex"}}>
              <Tooltip title={"If your box is oversized, please ensure it meets all of Amazons rules for oversized products (click to open Amazon help article)"}>
                <IconInfo style={{ marginRight: "6px" }} />
              </Tooltip>
            </a>
            )}

            {setAsDefaultControls && (<label style={{cursor: "pointer"}}>
              <RadioInput
                value="0"
                checked={isDefault}
                onChange={(value: string) => { if(makeDefault) makeDefault(); }}
              />
              Set As Default
            </label>)}
          </Flex>
        </Flex>
        {children}
      </Flex>
    </Flex>
  );
}
